import type {CSSProperties} from 'react';
import {WEDGE_TAB_BAR as F} from '@territory/tokens';
import {wedgeExactRole as r} from './wedgeRoles.js';

export type WedgeTabBarLook = 'tabbar-tab' | 'tabbar-tab-native' | 'tabbar-fab' | 'tabbar-coin';

const TAB_BAR_LOOKS = new Set<string>(['tabbar-tab', 'tabbar-tab-native', 'tabbar-fab', 'tabbar-coin']);
export const isWedgeTabBarLook = (look: string | undefined): look is WedgeTabBarLook => look !== undefined && TAB_BAR_LOOKS.has(look);

/* Released phone dock (the floating glass capsule at the foot of the phone shell). A tab is a flex 1 column that centers its pill and its label
   with a 1px gap, clips its overflow and takes its ink from the --wedge-tabbar-ink role: the caller sets the chosen hue or the tertiary ink.
   The native variant adds the three declarations that stop an iOS long press from arming a text selection or the callout. The raised center
   action is a 56px round disc on the label fill with a 4px rim in the page fill, placed by its wrapper. The Go anywhere coin is a 34px round disc
   on the accent fill with a 2px side margin. The two discs paint fixed shadows. The legacy declares no padding on any of them and neither does
   the look. Each keeps the declaration order of the source and reads the exact legacy roles. None draws a transparent target or an anchor
   (wedgeTabBarDrawsHit): the tabs fill the capsule edge to edge at its 58px height, and the coin keeps its legacy 34px box (Thor ruling
   SE-6a/SE-6b: no 44px change), so a region would reach the neighbor tab. */
export const wedgeTabBarDrawsHit = (look: string): boolean => !isWedgeTabBarLook(look);

export function wedgeTabBarStyle(look: WedgeTabBarLook): CSSProperties {
  switch (look) {
    case 'tabbar-tab':
    case 'tabbar-tab-native': return {
      flex: '1 1 0', minWidth: 0, background: 'none', border: 0, padding: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1,
      color: 'var(--wedge-tabbar-ink)', cursor: 'pointer', fontFamily: r('font'),
      position: 'relative', overflow: 'hidden',
      ...(look === 'tabbar-tab-native' ? {WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none'} : {}),
      WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
      transition: `color 180ms ${r('ease')}`,
    };
    case 'tabbar-fab': return {
      position: 'absolute', top: -20, width: 56, height: 56, borderRadius: '50%',
      background: r('label'), color: r('bg'), border: `4px solid ${r('bg')}`,
      cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: F.fabShadow,
    };
    case 'tabbar-coin': return {
      alignSelf: 'center', flexShrink: 0, width: 34, height: 34, borderRadius: '50%', marginLeft: 2, marginRight: 2,
      background: r('accent'), color: r('onAccent'), border: 'none', cursor: 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: F.coinShadow,
    };
  }
}
